import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import { Horse } from '@phosphor-icons/react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { SMART_COLORS as COLORS, smartCardSx as cardSx } from '../../theme/smartStableTheme';

const apiUrl = process.env.REACT_APP_API_URL;

const COPY = {
  ar: {
    title: 'تفعيل وتعطيل الخيل',
    subtitle: 'إدارة حالة ظهور الخيل في النظام. التعطيل لا يحذف البيانات، بل يخفي الخيل من العمليات اليومية ويمكن تفعيله لاحقا.',
    search: 'بحث باسم الخيل أو المالك أو السلالة أو اللون',
    status: 'الحالة',
    activeOnly: 'النشطة فقط',
    inactiveOnly: 'المعطلة فقط',
    all: 'الكل',
    apply: 'تطبيق',
    refresh: 'تحديث',
    export: 'تصدير JSON',
    activate: 'تفعيل',
    deactivate: 'تعطيل',
    activateSelected: 'تفعيل المحدد',
    deactivateSelected: 'تعطيل المحدد',
    total: 'إجمالي السجلات',
    active: 'نشطة',
    inactive: 'معطلة',
    selected: 'محدد',
    horse: 'الخيل',
    owner: 'المالك',
    breed: 'السلالة',
    color: 'اللون',
    disabledAt: 'تاريخ التعطيل',
    actions: 'الإجراءات',
    noData: 'لا توجد بيانات وفق المرشحات المختارة',
    loadError: 'فشل تحميل قائمة الخيل',
    activated: 'تم تفعيل الخيل',
    deactivated: 'تم تعطيل الخيل',
    activateFailed: 'تعذر تفعيل بعض السجلات',
    deactivateFailed: 'تعذر تعطيل بعض السجلات',
    exported: 'تم التصدير',
    exportFailed: 'فشل التصدير',
    rowsPerPage: 'صفوف لكل صفحة',
  },
  en: {
    title: 'Horse Activation',
    subtitle: 'Manage whether horses are active in daily operations. Deactivation does not delete data; it hides the horse until activated again.',
    search: 'Search by horse, owner, breed, or color',
    status: 'Status',
    activeOnly: 'Active only',
    inactiveOnly: 'Inactive only',
    all: 'All',
    apply: 'Apply',
    refresh: 'Refresh',
    export: 'Export JSON',
    activate: 'Activate',
    deactivate: 'Deactivate',
    activateSelected: 'Activate selected',
    deactivateSelected: 'Deactivate selected',
    total: 'Total records',
    active: 'Active',
    inactive: 'Inactive',
    selected: 'Selected',
    horse: 'Horse',
    owner: 'Owner',
    breed: 'Breed',
    color: 'Color',
    disabledAt: 'Disabled at',
    actions: 'Actions',
    noData: 'No data matches the selected filters',
    loadError: 'Failed to load horses',
    activated: 'Horse activated',
    deactivated: 'Horse deactivated',
    activateFailed: 'Some records could not be activated',
    deactivateFailed: 'Some records could not be deactivated',
    exported: 'Exported',
    exportFailed: 'Export failed',
    rowsPerPage: 'Rows per page',
  },
};

const isInactive = (row) => Number(row?.is_active ?? 1) !== 1;
const isActive = (row) => !isInactive(row);

function normalizeRows(arr) {
  return (Array.isArray(arr) ? arr : []).map((horse) => ({
    id: horse.id ?? horse.horse_id,
    name:
      horse.name ||
      horse.horse_name ||
      horse.horse_name_ar ||
      horse.horse_name_en ||
      `#${horse.id ?? horse.horse_id}`,
    owner: horse.owner_name || '',
    breed: horse.breed_arabic || horse.breed_english || horse.breed_name || '',
    color: horse.color_arabic || horse.color_english || horse.color_name || '',
    disabledAt: horse.modified_at || horse.deleted_at || horse.deletedDate || horse.deleted_on || '',
    is_active: Number(horse.is_active ?? 1),
    is_deleted: Number(horse.is_deleted ?? 0),
    raw: horse,
  }));
}

function MetricCard({ icon, title, value, helper, color = COLORS.gold, soft = COLORS.goldSoft }) {
  return (
    <Paper elevation={0} sx={{ ...cardSx, p: 2, minHeight: 104 }}>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Avatar sx={{ bgcolor: soft, color, width: 48, height: 48 }}>{icon}</Avatar>
        <Box>
          <Typography sx={{ color, fontWeight: 800 }}>{title}</Typography>
          <Typography variant="h5" sx={{ color: COLORS.text, fontWeight: 900 }}>{value}</Typography>
          {helper && <Typography variant="caption" sx={{ color: COLORS.muted }}>{helper}</Typography>}
        </Box>
      </Stack>
    </Paper>
  );
}

export default function RestoreDeletedHorses() {
  const { i18n } = useTranslation();
  const text = COPY[i18n.language === 'ar' ? 'ar' : 'en'];

  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('inactive');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const abortRef = useRef(null);
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(0);
  const [rpp, setRpp] = useState(10);
  const [snackbar, setSnackbar] = useState({ open: false, msg: '', sev: 'success' });

  const showSnack = (msg, sev = 'success') => setSnackbar({ open: true, msg, sev });

  const allChecked = rows.length > 0 && selected.length === rows.length;
  const someChecked = selected.length > 0 && selected.length < rows.length;

  const canActivateSelected = useMemo(
    () => selected.some((id) => isInactive(rows.find((row) => row.id === id))),
    [rows, selected]
  );
  const canDeactivateSelected = useMemo(
    () => selected.some((id) => isActive(rows.find((row) => row.id === id))),
    [rows, selected]
  );

  const pagedRows = useMemo(() => rows.slice(page * rpp, page * rpp + rpp), [page, rows, rpp]);

  const summary = useMemo(
    () => ({
      active: rows.filter(isActive).length,
      inactive: rows.filter(isInactive).length,
      total: rows.length,
    }),
    [rows]
  );

  const loadList = useCallback(
    async (is_active) => {
      const response = await axios.get(`${apiUrl}/horseslist`, {
        params: { is_deleted: 0, is_active, q: q || undefined },
        signal: abortRef.current?.signal,
      });
      return normalizeRows(response.data);
    },
    [q]
  );

  const loadRows = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      abortRef.current?.abort?.();
      abortRef.current = new AbortController();

      let data = [];
      if (filter === 'inactive') data = await loadList(0);
      else if (filter === 'active') data = await loadList(1);
      else {
        const [activeRows, inactiveRows] = await Promise.all([loadList(1), loadList(0)]);
        data = [...activeRows, ...inactiveRows].sort((a, b) => Number(b.id || 0) - Number(a.id || 0));
      }

      setRows(data);
      setSelected([]);
      setPage(0);
    } catch (error) {
      if (!axios.isCancel(error)) {
        setErr(error?.response?.data?.message || error?.response?.data?.error || text.loadError);
      }
    } finally {
      setLoading(false);
    }
  }, [filter, loadList, text.loadError]);

  useEffect(() => {
    loadRows();
    return () => abortRef.current?.abort?.();
  }, [loadRows]);

  const activateOne = async (id) => {
    try {
      await axios.put(`${apiUrl}/horses/${id}`, { is_active: 1 });
      return true;
    } catch {
      return false;
    }
  };

  const deactivateOne = async (id) => {
    try {
      await axios.put(`${apiUrl}/horses/${id}`, { is_active: 0 });
      return true;
    } catch {
      return false;
    }
  };

  const handleActivate = async (ids) => {
    if (!ids?.length) return;
    const results = await Promise.allSettled(ids.map(activateOne));
    const ok = results.filter((result) => result.status === 'fulfilled' && result.value).length;
    const fail = ids.length - ok;
    if (ok) showSnack(text.activated, 'success');
    if (fail) showSnack(text.activateFailed, 'warning');
    await loadRows();
  };

  const handleDeactivate = async (ids) => {
    if (!ids?.length) return;
    const results = await Promise.allSettled(ids.map(deactivateOne));
    const ok = results.filter((result) => result.status === 'fulfilled' && result.value).length;
    const fail = ids.length - ok;
    if (ok) showSnack(text.deactivated, 'success');
    if (fail) showSnack(text.deactivateFailed, 'warning');
    await loadRows();
  };

  const handleExportJSON = () => {
    try {
      const blob = new Blob([JSON.stringify(rows, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `horses_activation_${filter}_${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      showSnack(text.exported);
    } catch {
      showSnack(text.exportFailed, 'error');
    }
  };

  const handleSelectAll = (checked) => {
    setSelected(checked ? rows.map((row) => row.id) : []);
  };

  const handleSelectRow = (id, checked) => {
    setSelected((prev) => (checked ? [...prev, id] : prev.filter((item) => item !== id)));
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: COLORS.page, p: { xs: 2, md: 3 }, pb: 6 }}>
      <Paper elevation={0} sx={{ ...cardSx, p: { xs: 2, md: 3 }, mb: 3 }}>
        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'stretch', lg: 'center' }}>
          <Box>
            <Typography variant="h4" sx={{ color: COLORS.text, fontWeight: 900 }}>{text.title}</Typography>
            <Typography sx={{ color: COLORS.muted, mt: 0.5, maxWidth: 820 }}>{text.subtitle}</Typography>
          </Box>
          <Stack direction="row" spacing={1} justifyContent={{ xs: 'flex-start', lg: 'flex-end' }}>
            <Tooltip title={text.refresh}>
              <span>
                <IconButton onClick={loadRows} disabled={loading} sx={{ border: `1px solid ${COLORS.line}`, color: COLORS.gold }}>
                  <RefreshIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={text.export}>
              <span>
                <IconButton onClick={handleExportJSON} disabled={!rows.length} sx={{ border: `1px solid ${COLORS.line}`, color: COLORS.text }}>
                  <DownloadIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Stack>
      </Paper>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
        <MetricCard icon={<Horse size={26} weight="fill" />} title={text.total} value={summary.total} />
        <MetricCard icon={<ToggleOnIcon />} title={text.active} value={summary.active} color={COLORS.green} soft={COLORS.greenSoft} />
        <MetricCard icon={<ToggleOffIcon />} title={text.inactive} value={summary.inactive} color={COLORS.rose} soft={COLORS.roseSoft} />
        <MetricCard icon={<Checkbox checked={Boolean(selected.length)} disabled />} title={text.selected} value={selected.length} color={COLORS.blue} soft={COLORS.blueSoft} />
      </Box>

      <Paper elevation={0} sx={{ ...cardSx, p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }}>
          <TextField
            fullWidth
            size="small"
            placeholder={text.search}
            value={q}
            onChange={(event) => setQ(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 220 } }}>
            <InputLabel>{text.status}</InputLabel>
            <Select label={text.status} value={filter} onChange={(event) => setFilter(event.target.value)}>
              <MenuItem value="inactive">{text.inactiveOnly}</MenuItem>
              <MenuItem value="active">{text.activeOnly}</MenuItem>
              <MenuItem value="both">{text.all}</MenuItem>
            </Select>
          </FormControl>
          <Button variant="contained" onClick={loadRows} sx={{ bgcolor: COLORS.gold, '&:hover': { bgcolor: COLORS.goldHover }, minWidth: 110 }}>
            {text.apply}
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          <Button
            startIcon={<PlayCircleOutlineIcon />}
            disabled={!selected.length || !canActivateSelected}
            onClick={() => handleActivate(selected)}
            sx={{ color: COLORS.green }}
          >
            {text.activateSelected}
          </Button>
          <Button
            startIcon={<PauseCircleOutlineIcon />}
            disabled={!selected.length || !canDeactivateSelected}
            onClick={() => handleDeactivate(selected)}
            sx={{ color: COLORS.rose }}
          >
            {text.deactivateSelected}
          </Button>
        </Stack>
      </Paper>

      {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}

      {loading ? (
        <Stack spacing={1}>
          {Array.from({ length: 8 }).map((_, index) => (
            <Skeleton key={index} height={68} variant="rounded" />
          ))}
        </Stack>
      ) : rows.length === 0 ? (
        <Alert severity="info">{text.noData}</Alert>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ ...cardSx, overflow: 'hidden' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox checked={allChecked} indeterminate={someChecked} onChange={(event) => handleSelectAll(event.target.checked)} />
                </TableCell>
                <TableCell>#</TableCell>
                <TableCell>{text.horse}</TableCell>
                <TableCell>{text.owner}</TableCell>
                <TableCell>{text.breed}</TableCell>
                <TableCell>{text.color}</TableCell>
                <TableCell>{text.status}</TableCell>
                <TableCell>{text.disabledAt}</TableCell>
                <TableCell align="center">{text.actions}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pagedRows.map((row, index) => {
                const inactive = isInactive(row);
                return (
                  <TableRow key={row.id} hover sx={{ '& .MuiTableCell-root': { borderBottom: `1px solid ${COLORS.line}` } }}>
                    <TableCell padding="checkbox">
                      <Checkbox checked={selected.includes(row.id)} onChange={(event) => handleSelectRow(row.id, event.target.checked)} />
                    </TableCell>
                    <TableCell>{page * rpp + index + 1}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1.2} alignItems="center">
                        <Avatar sx={{ bgcolor: inactive ? COLORS.roseSoft : COLORS.goldSoft, color: inactive ? COLORS.rose : COLORS.gold, width: 38, height: 38 }}>
                          <Horse size={22} weight="fill" />
                        </Avatar>
                        <Box>
                          <Typography sx={{ color: COLORS.text, fontWeight: 900 }}>{row.name}</Typography>
                          <Typography variant="caption" sx={{ color: COLORS.muted }}>ID {row.id}</Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>{row.owner || '-'}</TableCell>
                    <TableCell>{row.breed || '-'}</TableCell>
                    <TableCell>{row.color || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        icon={inactive ? <ToggleOffIcon /> : <ToggleOnIcon />}
                        label={inactive ? text.inactive : text.active}
                        sx={{
                          bgcolor: inactive ? COLORS.roseSoft : COLORS.greenSoft,
                          color: inactive ? COLORS.rose : COLORS.green,
                          fontWeight: 900,
                        }}
                      />
                    </TableCell>
                    <TableCell>{inactive ? row.disabledAt || '-' : '-'}</TableCell>
                    <TableCell align="center">
                      {inactive ? (
                        <Tooltip title={text.activate}>
                          <IconButton onClick={() => handleActivate([row.id])} sx={{ color: COLORS.green }}>
                            <PlayCircleOutlineIcon />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title={text.deactivate}>
                          <IconButton onClick={() => handleDeactivate([row.id])} sx={{ color: COLORS.rose }}>
                            <PauseCircleOutlineIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={rows.length}
            page={page}
            onPageChange={(_, nextPage) => setPage(nextPage)}
            rowsPerPage={rpp}
            onRowsPerPageChange={(event) => {
              setRpp(parseInt(event.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage={text.rowsPerPage}
          />
        </TableContainer>
      )}

      <Snackbar
        open={snackbar.open}
        onClose={() => setSnackbar((state) => ({ ...state, open: false }))}
        autoHideDuration={snackbar.sev === 'error' ? 5000 : 2500}
      >
        <Alert severity={snackbar.sev} onClose={() => setSnackbar((state) => ({ ...state, open: false }))}>
          {snackbar.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
